"use client";

import { useEffect, useState, useRef } from "react";

const ONE_DAY_IN_MILLISECONDS = 86_400_000;
const ONE_HOUR_IN_MILLISECONDS = 3_600_000;
const ONE_MINUTE_IN_MILLISECONDS = 60_000;
const ONE_SECOND_IN_MILLISECONDS = 1000;

type CountdownUnit = {
  label: string;
  value: number | string;
};
const padWithZero = (value: number) => value.toString().padStart(2, "0");

export type CountdownProperties = {
  className?: string | undefined;
  separatorClassName?: string | undefined;
  unitBaseClassName?: string | undefined;
  unitLabelClassName?: string | undefined;
  unitValueClassName?: string | undefined;
  units: CountdownUnit[];
};
export const getUnitsOfTime = (countDown: number) => {
  const days = Math.floor(countDown / ONE_DAY_IN_MILLISECONDS);
  const hours = Math.floor(
    (countDown % ONE_DAY_IN_MILLISECONDS) / ONE_HOUR_IN_MILLISECONDS,
  );
  const minutes = Math.floor(
    (countDown % ONE_HOUR_IN_MILLISECONDS) / ONE_MINUTE_IN_MILLISECONDS,
  );
  const seconds = Math.floor(
    (countDown % ONE_MINUTE_IN_MILLISECONDS) / ONE_SECOND_IN_MILLISECONDS,
  );

  return {
    days: days >= 0 ? padWithZero(days) : "00",
    daysAndHours: days >= 0 ? padWithZero(days * 24 + hours) : "00",
    hours: hours >= 0 ? padWithZero(hours) : "00",
    minutes: minutes >= 0 ? padWithZero(minutes) : "00",
    seconds: seconds >= 0 ? padWithZero(seconds) : "00",
  };
};
export const useCountdown = (targetDate: number | Date) => {
  const [remainingTime, setRemainingTime] = useState(
    new Date(targetDate).getTime() - Date.now(),
  );
  const intervalId = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    intervalId.current = setInterval(() => {
      const timeDiff = new Date(targetDate).getTime() - Date.now();

      if (timeDiff <= 0) {
        clearInterval(intervalId.current ?? undefined);
        setRemainingTime(0);
        return;
      }

      setRemainingTime(timeDiff);
    }, 1000);

    return () => {
      clearInterval(intervalId.current ?? undefined);
    };
  }, [targetDate]);

  const isCountdownComplete = remainingTime <= 0;
  return { ...getUnitsOfTime(remainingTime), isCountdownComplete };
};
